import React, { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Star, ShoppingBag, ImageOff } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";

const ProductCard = ({ product, index }) => {
  const { addToCart } = useCart();
  const [isWishlisted, setIsWishlisted] = useState(false);
  // Nuevo estado para manejar errores de imagen
  const [imageError, setImageError] = useState(false);

  // Componente que se renderiza cuando la imagen falla
  const NoImagePlaceholder = () => (
    <div className="w-full h-64 flex items-center justify-center bg-gray-100 text-gray-400">
      <ImageOff className="w-12 h-12" />
      <span className="sr-only">No hay imagen</span>
    </div>
  );

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };

  const toggleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWishlisted(!isWishlisted);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      whileHover={{ y: -6, scale: 1.02 }}
      className="group"
    >
      <Link to={`/product/${product.id}`}>
        {/*
          IMPORTANTE: Se añade 'min-h-[460px]' al contenedor principal para forzar una altura mínima.
          Ajusta este valor si el contenido total lo requiere.
        */}
        <div className="bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 relative min-h-[460px] flex flex-col justify-between">
          
          {/* Imagen */}
          <div className="relative overflow-hidden">
            {imageError ? (
              // 2. Imagen por defecto si falla la carga
              <NoImagePlaceholder />
            ) : (
              // 1. Manejo de error de carga de imagen
              <motion.img
                src={product.image}
                alt={product.name}
                onError={() => setImageError(true)} // Captura el error de imagen
                className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-110 group-hover:rotate-1"
              />
            )}

            {/* ... Badges, Wishlist y Agotado (Contenido sin cambios) ... */}
            
            {/* Badge OFERTA */}
            {product.originalPrice > product.price && (
              <div className="absolute top-3 left-3">
                <span className="px-3 py-1 text-xs font-bold rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white shadow">
                  OFERTA
                </span>
              </div>
            )}

            {/* Wishlist */}
            <motion.button
              onClick={toggleWishlist}
              className="absolute top-3 right-3 p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all duration-300 z-10"
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.9 }}
            >
              <Heart
                className={`w-4 h-4 transition-colors ${isWishlisted ? "fill-red-500 text-red-500" : "text-gray-600"
                  }`}
              />
            </motion.button>

            {/* Agotado */}
            {!product.inStock && (
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center">
                <span className="bg-gray-800/90 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Agotado
                </span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="p-5 space-y-3">
            {/* Rating */}
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${i < product.rating
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-gray-300"
                      }`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-500">
                ({product.reviews})
              </span>
            </div>

            {/* Nombre - Altura ajustada por line-clamp-2 */}
            <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 h-12 overflow-hidden group-hover:text-blue-600 transition-colors">
              {product.name}
            </h3>

            {/* Descripción - Altura fija por line-clamp-2 */}
            <p className="text-gray-600 text-sm line-clamp-2 h-10 overflow-hidden">
              {product.description}
            </p>

            {/* Precio + Botón */}
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-gray-900">
                  ${product.price}
                </span>
                {product.originalPrice > product.price && (
                  <span className="text-sm text-gray-400 line-through">
                    ${product.originalPrice}
                  </span>
                )}
              </div>

              <motion.button
                onClick={handleAddToCart}
                disabled={!product.inStock}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all duration-300 shadow ${product.inStock
                    ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                whileHover={product.inStock ? { scale: 1.05 } : {}}
                whileTap={product.inStock ? { scale: 0.95 } : {}}
              >
                <ShoppingBag className="w-4 h-4" />
                <span className="hidden sm:block">
                  {product.inStock ? "Agregar" : "Agotado"}
                </span>
              </motion.button>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;